import { RowData } from "@tanstack/react-table"
import { format, subDays } from "date-fns"
import { useCallback, useMemo, useState } from "react"
import { NavLink } from "react-router-dom"
import { DateRangeFilter } from "../../component/filters/date-range-filter.tsx"
import { Column, ReactTailwindTable, useTable } from "../../component/react-tailwind-table"
import { useFilters } from "../../component/use-filter"
import { OperatorCallTypeReport } from "./operator-call-type-report.tsx"
import {
  useGetClientTypeDistributionQuery,
  useGetOperatorDataQuery,
  useGetResultPerformanceSummaryQuery,
  useGetSummaryStatisticsQuery,
  useGetSummaryTotalStatisticsQuery,
  useGetWeeklyAveragesQuery,
} from "./service"
import { IntentDirectionFilter } from "../../component/filters/intent-direction-filter.tsx"
import { useSelection } from "../../app/department-context.tsx"

const clientTypeDistributionColumns: Column<RowData>[] = [
  { header: "Тип клиента", accessorKey: "type", enableSorting: false },
  { header: "Количество", accessorKey: "count", enableSorting: false },
  { header: "Доля", accessorKey: "percentage", enableSorting: false, cell: ({ row }) => `${row.original.percentage}%` },
  { header: "Средний балл", accessorKey: "averageScore", enableSorting: false },
]

const resultPerformanceSummaryColumns: Column<RowData>[] = [
  { header: "Тип звонка", accessorKey: "callType", enableSorting: false },
  {
    header: "Средняя по звонкам",
    enableSorting: false,
    accessorKey: "average",
    cell: ({ row }) => `${row.original.average}%`,
  },
  {
    header: "Лучшая результативность",
    enableSorting: false,
    accessorKey: "max",
    cell: ({ row }) => `${row.original.max}%`,
  },
  {
    header: "Худшая результативность",
    enableSorting: false,
    accessorKey: "min",
    cell: ({ row }) => `${row.original.min}%`,
  },
]

const weeklyAveragesColumns: Column<RowData>[] = [
  { header: "Оператор", accessorKey: "operator", enableSorting: false },
  { header: "Неделя 1", accessorKey: "week1", enableSorting: false },
  { header: "Неделя 2", accessorKey: "week2", enableSorting: false },
  { header: "Неделя 3", accessorKey: "week3", enableSorting: false },
  { header: "Неделя 4", accessorKey: "week4", enableSorting: false },
  { header: "Тренд", accessorKey: "trend", enableSorting: false },
  { header: "Изменение", accessorKey: "change", enableSorting: false },
]

export const Dashboard = () => {
  const { selectedDepartment } = useSelection()
  const { filters, updateFilters } = useFilters({
    params: {
      startDate: "string",
      endDate: "string",
      departmentId: "number",
      intentDirection: "string",
    },
    defaults: {
      departmentId: selectedDepartment?.id,
      startDate: format(subDays(new Date(), 30), "yyyy-MM-dd"),
      endDate: format(new Date(), "yyyy-MM-dd"),
      intentDirection: "",
    },
  })
  const { data: summaryStatistics, isLoading: summaryStatisticsLoading } = useGetSummaryStatisticsQuery(filters, {
    skip: filters.departmentId < 0,
  })
  const { data: summaryTotalStatistics, isLoading: summaryTotalStatisticsLoading } =
    useGetSummaryTotalStatisticsQuery(filters)
  const clientTypeDistributionTable = useTable({ defaultPageSize: 10 })
  const operatorDataTable = useTable({ defaultPageSize: 10 })
  const weeklyAveragesTable = useTable({ defaultPageSize: 10 })
  const resultPerformanceSummaryTable = useTable({ defaultPageSize: 10 })
  const [expandedRows, setExpandedRows] = useState<string[]>([])

  const toggleRow = useCallback((rowId: string) => {
    setExpandedRows(prev => (prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId]))
  }, [])

  const operatorDataColumns: Column<any>[] = useMemo(
    () => [
      {
        id: "expander",
        size: 50,
        header: () => null,
        enableSorting: false,
        cell: ({ row }) => (
          <button onClick={() => toggleRow(row.id)}>{expandedRows.includes(row.id) ? "-" : "+"}</button>
        ),
      },
      {
        header: "Оператор",
        accessorKey: "name",
        enableSorting: false,
        cell: ({ row }) => (
          <NavLink
            className={"underline"}
            to={`/app/operator-report?operatorId=${row.original.operator_id}&startDate=${filters.startDate}&endDate=${filters.endDate}`}
          >
            {row.original.name}
          </NavLink>
        ),
      },
      { header: "Общий балл", accessorKey: "totalScore" },
      { header: "Оценено звонков", accessorKey: "callCount" },
      { header: "Суммарная длительность", accessorKey: "totalCallDuration" },
      { header: "Средняя длительность", accessorKey: "averageCallDuration" },
    ],
    [filters.endDate, expandedRows, filters.startDate, toggleRow],
  )

  return (
    <div>
      <div className="max-w-6xl mx-auto dark:bg-base-200 p-6 sm:p-8 rounded-lg shadow-sm">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-teal-800 mb-2">
          ОТЧЕТ ПО ОЦЕНКЕ РАБОТЫ КОЛЛ-ЦЕНТРА
        </h1>
        <p className="text-center text-gray-500 mb-6 sm:mb-8">
          Период оценки: {filters.startDate} - {filters.endDate}
        </p>

        <div className="flex justify-center gap-4 mb-8">
          <DateRangeFilter
            startDate={filters.startDate}
            endDate={filters.endDate}
            onStartDateChange={startDate => updateFilters({ startDate })}
            onEndDateChange={endDate => updateFilters({ endDate })}
          />
          <IntentDirectionFilter
            intentDirection={filters.intentDirection}
            onIntentDirectionChange={intentDirection => updateFilters({ intentDirection })}
          />
        </div>

        <div className="mb-8">
          <h2 className="text-lg sm:text-xl font-semibold text-teal-700 mb-4 border-b border-b-teal-500 pb-2">
            1. Общая оценка колл-центра
          </h2>
          <div className="mb-6">
            <h3 className="text-base sm:text-lg font-semibold text-teal-600 mb-3">1.1 Сводная статистика</h3>
            {(summaryStatisticsLoading || summaryTotalStatisticsLoading) && <p>Loading...</p>}
            {summaryStatistics && summaryTotalStatistics && (
              <table className="min-w-full text-sm sm:text-base">
                <thead className="bg-teal-100 dark:bg-teal-950 text-teal-800 dark:text-teal-700">
                  <tr>
                    <th className="py-2 px-3 sm:px-4 text-left font-medium ">Показатель</th>
                    <th className="py-2 px-3 sm:px-4 text-left font-medium ">Значение</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: "Общее количество обращений", value: summaryTotalStatistics.totalCalls },
                    { label: "Количество пропущенных обращений", value: summaryTotalStatistics.missedCalls },
                    { label: "Количество оцененных звонков", value: summaryStatistics.ratedCalls },
                    { label: "Средний балл по колл-центру", value: `${summaryStatistics.averageScore} из 10` },
                    { label: "Процент звонков выше 8 баллов", value: `${summaryStatistics.percentageAbove8}%` },
                    { label: "Процент звонков ниже 6 баллов", value: `${summaryStatistics.percentageBelow6}%` },
                    { label: "Суммарная длительность звонков", value: summaryStatistics.totalCallDuration },
                    { label: "Средняя длительность звонка", value: summaryStatistics.averageCallDuration },
                  ].map(row => (
                    <tr key={row.label} className="odd:bg-base even:bg-accent">
                      <td className="px-3 sm:px-4 py-2">{row.label}</td>
                      <td className="px-3 sm:px-4 py-2">{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="mb-6">
            <h3 className="text-base sm:text-lg font-semibold text-teal-600 mb-3">
              1.2 Распределение по типам клиентов
            </h3>
            <ReactTailwindTable
              columns={clientTypeDistributionColumns}
              query={useGetClientTypeDistributionQuery}
              queryParams={{ ...filters }}
              visibilityOptions={{
                disablePagination: true,
              }}
              {...clientTypeDistributionTable}
              tableId="clientTypeDistributionTable"
            />
          </div>

          <div className="mt-6">
            <h3 className="text-base sm:text-lg font-semibold text-teal-600 mb-3">1.3 Сводка результативности</h3>
            <ReactTailwindTable
              columns={resultPerformanceSummaryColumns}
              query={useGetResultPerformanceSummaryQuery}
              queryParams={{ ...filters }}
              visibilityOptions={{
                disablePagination: true,
              }}
              {...resultPerformanceSummaryTable}
              tableId="resultPerformanceSummaryTable"
            />
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-lg sm:text-xl font-semibold text-teal-700 mb-4 border-b border-b-teal-500 pb-2">
            2. Оценка по каждому оператору
          </h2>
          <ReactTailwindTable
            columns={operatorDataColumns}
            query={useGetOperatorDataQuery}
            queryParams={{ ...filters }}
            {...operatorDataTable}
            renderRowSubComponent={({ row }) => (
              <tr>
                <td colSpan={operatorDataColumns.length}>
                  <OperatorCallTypeReport
                    filters={{
                      ...filters,
                      operatorId: row.original.operator_id,
                    }}
                  />
                </td>
              </tr>
            )}
            expandedRows={expandedRows}
            visibilityOptions={{
              disablePagination: true,
            }}
            tableId="operatorDataTable"
          />
        </div>

        <div>
          <h2 className="text-lg sm:text-xl font-semibold text-teal-700 mb-4 border-b border-b-teal-500 pb-2">
            5.2 Еженедельные средние показатели
          </h2>
          <ReactTailwindTable
            columns={weeklyAveragesColumns}
            query={useGetWeeklyAveragesQuery}
            queryParams={{ ...filters }}
            visibilityOptions={{
              disablePagination: true,
            }}
            {...weeklyAveragesTable}
            tableId="weeklyAveragesTable"
          />
        </div>
      </div>
    </div>
  )
}
